import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Filter,
  Activity,
  Zap
} from 'lucide-react';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { usageService } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function StatCard({ icon: Icon, iconBg, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, fmtVal }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white rounded-xl px-3 py-2.5 shadow-xl text-xs min-w-[120px]">
      <p className="text-slate-400 mb-1.5 font-medium">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span style={{ color: p.color }} className="font-semibold">
            {fmtVal ? fmtVal(p.value, p.name) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const Usage = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [modelUsage, setModelUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('365d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchUsageData();
  }, [dateRange, startDate, endDate]);

  const fetchUsageData = async () => {
    try {
      setLoading(true);

      let start, end;
      if (dateRange === 'custom' && startDate && endDate) {
        start = startDate;
        end = endDate;
      } else {
        const now = new Date();
        const days = dateRange === 'all' ? 365 : (parseInt(dateRange.replace('d', '')) || 365);
        start = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
      }

      const [overview, trends, models] = await Promise.all([
        usageService.getUsageOverview(start, end),
        usageService.getUsageTrends(isNaN(parseInt(dateRange.replace('d', ''))) ? 365 : parseInt(dateRange.replace('d', ''))),
        usageService.getModelUsage(start, end)
      ]);

      setOverviewData(overview.data.data);
      setTrendsData(trends.data.data.trends);
      setModelUsage(models.data.data.models);
    } catch (error) {
      toast.error('Failed to fetch usage data');
      console.error('Usage data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(numAmount || 0);
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading usage data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usage Analytics</h1>
          <p className="text-slate-500 text-sm mt-0.5">Track API key usage and token consumption</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-600">Range:</span>
          </div>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="365d">Last 365 days</option>
            <option value="all">All time</option>
            <option value="custom">Custom range</option>
          </select>
          {dateRange === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
              <span className="text-slate-400 text-sm">to</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            </>
          )}
          <button onClick={fetchUsageData}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
            <Filter size={14} />
            Apply
          </button>
        </div>
      </div>

      {/* Overview stat cards */}
      {overviewData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={BarChart3} iconBg="bg-indigo-500" label="Total Requests"
            value={formatNumber(overviewData.overview?.total_requests || 0)} />
          <StatCard icon={TrendingUp} iconBg="bg-emerald-500" label="Total Tokens"
            value={formatNumber(overviewData.overview?.total_tokens || 0)} />
          <StatCard icon={Activity} iconBg="bg-blue-500" label="Input Tokens"
            value={formatNumber(overviewData.overview?.total_input_tokens || 0)} />
          <StatCard icon={Zap} iconBg="bg-violet-500" label="Output Tokens"
            value={formatNumber(overviewData.overview?.total_output_tokens || 0)} />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Trends */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Usage Trends</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trendsData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="uTokensGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="uCostGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date"
                tickFormatter={v => new Date(v).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                tickFormatter={v => formatNumber(v)} />
              <Tooltip content={<CustomTooltip fmtVal={(v, n) => n === 'Tokens' ? formatNumber(v) : formatCurrency(v)} />} />
              <Area type="monotone" dataKey="total_tokens" stroke="#6366f1" strokeWidth={2}
                fill="url(#uTokensGrad)" dot={false} name="Tokens" />
              <Area type="monotone" dataKey="total_cost" stroke="#10b981" strokeWidth={2}
                fill="url(#uCostGrad)" dot={false} name="Cost" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Model Usage */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Model Usage</h3>
          {modelUsage && modelUsage.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={modelUsage} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barGap={4}>
                <defs>
                  <linearGradient id="uBarTokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#818cf8" stopOpacity={0.7} />
                  </linearGradient>
                  <linearGradient id="uBarCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="model_name"
                  tickFormatter={v => v.split('.').pop().replace(/-v\d.*/, '').slice(0, 12)}
                  tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  tickFormatter={v => formatNumber(v)} />
                <Tooltip content={<CustomTooltip fmtVal={(v, n) => n === 'Tokens' ? formatNumber(v) : formatCurrency(v)} />} />
                <Bar dataKey="total_tokens" fill="url(#uBarTokens)" name="Tokens" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="total_cost" fill="url(#uBarCost)" name="Cost" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
              No model usage data available
            </div>
          )}
        </div>
      </div>

      {/* Top API Keys Usage */}
      {overviewData?.topKeys && overviewData.topKeys.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Top API Keys by Usage</h3>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  {['Key Name', 'Requests', 'Input Tokens', 'Output Tokens', 'Total Tokens', 'Cost'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-50">
                {overviewData.topKeys.map((key, index) => (
                  <tr key={index} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{key.key_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(key.request_count)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(key.input_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(key.output_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(key.total_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatCurrency(key.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Models Usage */}
      {overviewData?.topModels && overviewData.topModels.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Top Models by Usage</h3>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  {['Model Name', 'Requests', 'Input Tokens', 'Output Tokens', 'Total Tokens', 'Cost'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-50">
                {overviewData.topModels.map((model, index) => (
                  <tr key={index} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{model.model_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(model.request_count)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(model.input_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(model.output_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatNumber(model.total_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{formatCurrency(model.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usage;
